import { type MouseEvent, type ReactElement, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Box } from '@/ui';
import {
  Autocomplete,
  DeleteIcon,
  IconButton,
  Menu,
  MenuItem,
  OutlinedInput as MuiOutlinedInput,
} from '@/ui/primitives';
import {
  formFieldPlaceholderTypographySx,
  formFieldTypographySx,
} from '@/ui/primitives/formFieldVariants';
import { defaultTagColor } from './EditableTagsSection.utils';
import type { TagColor } from '@/shared/data/tags';

const tagColorOptions: Array<{ value: TagColor; swatch: string }> = [
  { value: 'neutral', swatch: '#8D949E' },
  { value: 'green', swatch: '#43A047' },
  { value: 'orange', swatch: '#FB8C00' },
  { value: 'red', swatch: '#EF5350' },
  { value: 'blue', swatch: '#42A5F5' },
  { value: 'purple', swatch: '#AB47BC' },
  { value: 'teal', swatch: '#00ACC1' },
  { value: 'brown', swatch: '#8D6E63' },
];

const tagColorSwatchSize = '18px';
const tagColorSwatchRadius = '6px';
const tagColorControlSize = '32px';
const tagColorSelectWidth = tagColorControlSize;
const tagColorSwatchSx = {
  display: 'inline-flex',
  width: tagColorSwatchSize,
  height: tagColorSwatchSize,
  minWidth: tagColorSwatchSize,
  minHeight: tagColorSwatchSize,
  maxWidth: tagColorSwatchSize,
  maxHeight: tagColorSwatchSize,
  flex: '0 0 auto',
  flexShrink: 0,
  aspectRatio: '1 / 1',
  boxSizing: 'border-box',
  borderRadius: tagColorSwatchRadius,
  border: '1px solid',
  borderColor: 'divider',
} as const;

type EditableTagRowProps = {
  tag: string;
  color: TagColor | undefined;
  index: number;
  tagNameOptions: string[];
  onTagChange: (nextValue: string) => void;
  onColorChange: (nextValue: TagColor) => void;
  onRemove: () => void;
};

export const EditableTagRow = ({
  tag,
  color,
  index,
  tagNameOptions,
  onTagChange,
  onColorChange,
  onRemove,
}: EditableTagRowProps): ReactElement => {
  const [colorMenuAnchorEl, setColorMenuAnchorEl] = useState<HTMLElement | null>(null);
  const selectedColor = color ?? defaultTagColor;
  const selectedSwatch =
    tagColorOptions.find((option) => option.value === selectedColor)?.swatch ??
    tagColorOptions[0].swatch;

  const handleOpenColorMenu = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setColorMenuAnchorEl(event.currentTarget);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', gap: s('sm'), width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: `minmax(0, 1fr) ${tagColorSelectWidth}`,
          alignItems: 'stretch',
          gap: s('md'),
        }}
      >
        <Autocomplete
          freeSolo
          disableClearable
          options={tagNameOptions}
          value={tag}
          inputValue={tag}
          openOnFocus
          selectOnFocus
          autoHighlight
          clearOnBlur={false}
          handleHomeEndKeys
          onInputChange={(_event, nextValue, reason) => {
            if (reason === 'input' || reason === 'clear') {
              onTagChange(nextValue);
            }
          }}
          onChange={(_event, nextValue) => {
            onTagChange(typeof nextValue === 'string' ? nextValue : '');
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                key={key}
                component="li"
                {...optionProps}
                sx={{
                  px: s('md'),
                  minHeight: `calc(${s('2xl')} + ${s('md')})`,
                }}
              >
                {option}
              </Box>
            );
          }}
          renderInput={(params) => (
            <MuiOutlinedInput
              {...params.InputProps}
              inputProps={{
                ...params.inputProps,
                'aria-label': `Tag ${index + 1}`,
              }}
              inputRef={params.InputProps.ref}
              notched={false}
              fullWidth
              placeholder="Enter or select a tag"
              sx={(theme) => ({
                width: '100%',
                minHeight: `${tagColorControlSize}px !important`,
                height: `${tagColorControlSize}px !important`,
                maxHeight: `${tagColorControlSize}px !important`,
                boxSizing: 'border-box',
                backgroundColor: 'background.default',
                borderRadius: r('md'),
                ...formFieldTypographySx,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'divider',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '& .MuiOutlinedInput-input': {
                  height: '100%',
                  boxSizing: 'border-box',
                  paddingTop: s('none'),
                  paddingBottom: s('none'),
                  paddingLeft: s('md'),
                  paddingRight: s('md'),
                  fontSize: theme.typography.subtitle1.fontSize,
                  lineHeight: theme.typography.subtitle1.lineHeight,
                  border: '0 !important',
                  boxShadow: 'none !important',
                  textDecoration: 'none !important',
                  outline: '0 !important',
                  backgroundImage: 'none !important',
                },
                '& .MuiInputBase-input::placeholder, & .MuiOutlinedInput-input::placeholder, & input::placeholder':
                  {
                    ...formFieldPlaceholderTypographySx,
                  },
                '& input': {
                  ...formFieldTypographySx,
                  border: '0 !important',
                  boxShadow: 'none !important',
                  textDecoration: 'none !important',
                  outline: '0 !important',
                  backgroundImage: 'none !important',
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield',
                },
              })}
            />
          )}
          sx={{
            width: '100%',
            alignSelf: 'stretch',
            minHeight: `${tagColorControlSize}px`,
            height: `${tagColorControlSize}px`,
            maxHeight: `${tagColorControlSize}px`,
            display: 'flex',
            alignItems: 'stretch',
            '& .MuiAutocomplete-inputRoot': {
              minHeight: '100% !important',
              height: '100% !important',
              maxHeight: '100% !important',
              width: '100%',
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
              boxSizing: 'border-box',
            },
            '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
              paddingTop: '0 !important',
              paddingBottom: '0 !important',
              paddingLeft: `${s('lg')} !important`,
              paddingRight: `${s('lg')} !important`,
              margin: '0 !important',
              minWidth: 0,
            },
          }}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-paper': {
                  backgroundColor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: `${r('md')} !important`,
                  boxShadow: 'none',
                  backgroundImage: 'none',
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: `${r('md')} !important`,
                boxShadow: 'none',
                backgroundImage: 'none',
              },
            },
            listbox: {
              sx: {
                p: s('none'),
                m: s('none'),
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${s('md')} !important`,
                  paddingRight: `${s('md')} !important`,
                  whiteSpace: 'nowrap',
                },
              },
            },
          }}
        />
        <Box
          role="button"
          tabIndex={0}
          aria-label={`Choose color for tag ${index + 1}`}
          onClick={handleOpenColorMenu}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
              return;
            }
            event.preventDefault();
            event.stopPropagation();
            setColorMenuAnchorEl(event.currentTarget as HTMLElement);
          }}
          sx={{
            minWidth: tagColorSelectWidth,
            width: tagColorSelectWidth,
            maxWidth: tagColorSelectWidth,
            minHeight: tagColorControlSize,
            height: tagColorControlSize,
            maxHeight: tagColorControlSize,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: r('md'),
            backgroundColor: 'background.default',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <Box component="span" sx={[tagColorSwatchSx, { backgroundColor: selectedSwatch }]} />
        </Box>
      </Box>
      <Menu
        anchorEl={colorMenuAnchorEl}
        open={colorMenuAnchorEl !== null}
        onClose={() => {
          setColorMenuAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: tagColorSelectWidth,
              width: tagColorSelectWidth,
              maxWidth: tagColorSelectWidth,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: `${r('md')} !important`,
              backgroundColor: 'background.default',
              backgroundImage: 'none',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          },
          list: {
            sx: {
              p: s('none'),
              m: s('none'),
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        {tagColorOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              onColorChange(option.value);
              setColorMenuAnchorEl(null);
            }}
            sx={{
              minHeight: `calc(${s('2xl')} + ${s('xs')})`,
              px: s('md'),
              py: s('md'),
              justifyContent: 'center',
              borderRadius: r('none'),
              backgroundColor: selectedColor === option.value ? 'action.selected' : undefined,
            }}
          >
            <Box component="span" sx={[tagColorSwatchSx, { backgroundColor: option.swatch }]} />
          </MenuItem>
        ))}
      </Menu>
      <IconButton
        aria-label={`Remove tag ${index + 1}`}
        size="medium"
        sx={{
          width: `calc(${s('2xl')} + ${s('sm')})`,
          height: `calc(${s('2xl')} + ${s('sm')})`,
          minWidth: `calc(${s('2xl')} + ${s('sm')})`,
          minHeight: `calc(${s('2xl')} + ${s('sm')})`,
          p: s('none'),
          borderRadius: r('pill'),
          '&:hover, &.Mui-focusVisible': {
            backgroundColor: 'action.hover',
          },
        }}
        onClick={onRemove}
      >
        <DeleteIcon sx={{ color: 'error.main', fontSize: `calc(${s('2xl')} + ${s('xs')})` }} />
      </IconButton>
    </Box>
  );
};
